import React, {
  useState,
  useRef,
  forwardRef,
  useEffect,
  Ref,
  KeyboardEvent,
  SyntheticEvent,
  ReactNode,
} from 'react';
import { Box, Tag, InputBase, useTheme } from '@tonic-ui/react';

import { useCustomStyles } from '../../../styles';

interface EditableTagProps {
  value?: string;
  id?: string;
  isInvalid?: boolean;
  children: ReactNode;
  onChange: (event: SyntheticEvent) => void;
  onClose: () => void;
  onFocus: (e: SyntheticEvent) => void;
}

const EditableTag = forwardRef<Ref<HTMLInputElement>, EditableTagProps>(
  ({ value, id, isInvalid, onChange, onClose, ...rest }: EditableTagProps, ref) => {
    const { sizes } = useTheme();
    const { tagBackground, tagText, invalidBgColor } = useCustomStyles('support');
    const [inputVisible, setInputVisible] = useState(false);

    const tagInputFieldRef = useRef<HTMLInputElement>();
    const tagHiddenSpanRef = useRef<HTMLSpanElement>();
    const mt = `calc(${sizes['1x']} - 1px)`;

    const handleInputVisible = () => setInputVisible(true);
    const hideInput = () => setInputVisible(false);
    const handleInputBlur = (event: SyntheticEvent) => {
      onChange(event);
      hideInput();
    };

    const handleKeyUp = (keyboardEvt: KeyboardEvent<HTMLInputElement>) => {
      switch (keyboardEvt.key) {
        case 'Enter':
          onChange(keyboardEvt);
          hideInput();
          break;
        case 'Escape':
          hideInput();
          break;
      }
    };

    const resizeInputField = () => {
      const { current: inputField } = tagInputFieldRef || {};
      const { current: hiddenSpan } = tagHiddenSpanRef || {};

      if (!inputField || !hiddenSpan) return;

      hiddenSpan.textContent = inputField.value;
      inputField.style.width = `${hiddenSpan.offsetWidth}px`;
      inputField.focus();
    };

    useEffect(() => {
      resizeInputField();
    }, [inputVisible]);

    return inputVisible ? (
      <>
        <Box
          as="span"
          ref={tagHiddenSpanRef}
          position="absolute"
          height="0"
          overflow="hidden"
          whiteSpace="pre"
        />
        <InputBase
          data-id={`input-tag-${id}`}
          ref={tagInputFieldRef}
          maxWidth="334px"
          mr="2x"
          mt={mt}
          defaultValue={value}
          onKeyUp={handleKeyUp}
          onBlur={handleInputBlur}
          onInput={resizeInputField}
        />
      </>
    ) : (
      <Tag
        role="button"
        ref={ref}
        isClosable
        wordBreak="break-word"
        error={isInvalid}
        mr="2x"
        mt={mt}
        backgroundColor={tagBackground}
        color={tagText}
        onClick={(event: SyntheticEvent) => {
          event.stopPropagation();
          handleInputVisible();
        }}
        onClose={(event: SyntheticEvent) => {
          event.stopPropagation();
          return onClose();
        }}
        cursor="pointer"
        {...(isInvalid && {
          _hover: {
            '&:not(:focus)': {
              bg: invalidBgColor,
              '&::before': {
                bg: invalidBgColor,
              },
            },
          },
        })}
        {...rest}
      />
    );
  }
);

EditableTag.displayName = 'EditableTag';

export default EditableTag;
